import React, { useState, useEffect } from 'react';
import {
  Window,
  WindowHeader,
  WindowContent,
  Button,
  Fieldset,
  GroupBox,
  Separator,
  Toolbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeadCell,
  TableDataCell
} from 'react95';
import { useDebtAccounts, usePaymentArrangements, useTransactions, useClients } from '../hooks';
import { 
  calculateClientFinancialSummary,
  formatCurrency,
  calculatePaymentSchedule 
} from '../utils/financialCalculations';
import { formatCurrency as modelFormatCurrency } from '../models/DebtAccount';
import { TRANSACTION_TYPES } from '../models/TransactionHistory';

/**
 * Financial Summary Component
 * 
 * Features implemented according to task 5 requirements:
 * - Display debt amounts, payments, and balances
 * - Real-time balance calculations
 * - Display payment history and last payment information
 * - Show payment arrangement details
 * 
 * Requirements: 2.4, 3.5
 */

const FinancialSummary = ({ 
  clientId = null,
  onPaymentRecorded = () => {},
  onArrangementUpdated = () => {},
  onArrangementEdit = () => {},  // ADD THIS LINE
  showDetailedView = true,
  allClients = []
}) => {
  const debtAccounts = useDebtAccounts();
  const paymentArrangements = usePaymentArrangements();
  const transactions = useTransactions();
  
  console.log('FinancialSummary rendered with clientId:', clientId, 'Type:', typeof clientId);
  console.log('All clients with IDs:', allClients.map(client => ({ 
    id: client.id, 
    name: client.clientName, 
    code: client.debtCode 
  })));
  console.log('FinancialSummary hooks data:', {
    debtAccountsCount: debtAccounts.items.length,
    paymentArrangementsCount: paymentArrangements.items.length,
    transactionsCount: transactions.items.length,
    debtAccountsItems: debtAccounts.items,
    debtAccountClientIds: debtAccounts.items.map(account => ({ clientId: account.clientId, type: typeof account.clientId })),
    clientDebtAccounts: debtAccounts.items.filter(account => 
      account.clientId == clientId || account.clientId === String(clientId) || String(account.clientId) === String(clientId)
    )
  });
  
  // Component state
  const [selectedView, setSelectedView] = useState('summary'); // 'summary', 'accounts', 'arrangements', 'history'
  const [financialSummary, setFinancialSummary] = useState({
    totalDebtAmount: 0,
    totalAdjustments: 0,
    totalLegalFees: 0,
    totalInterest: 0,
    totalDebtFees: 0,
    totalPayments: 0,
    totalBalance: 0,
    totalArrangementAmount: 0,
    totalArrears: 0,
    accountCount: 0,
    activeArrangementCount: 0,
    lastPaymentDate: null,
    lastPaymentAmount: 0
  });
  
  // Client-specific data
  const [clientDebtAccounts, setClientDebtAccounts] = useState([]);
  const [clientArrangements, setClientArrangements] = useState([]);
  const [clientTransactions, setClientTransactions] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);

  // Load and calculate financial data when clientId or data changes
  useEffect(() => {
    if (clientId) {
      // Get client-specific data
      const accounts = debtAccounts.getDebtAccountsByClientId(clientId);
      const arrangements = paymentArrangements.getArrangementsByClientId(clientId);
      const allTransactions = transactions.getTransactionsByClientId(clientId);
      const payments = transactions.getPaymentTransactions(clientId);
      
      setClientDebtAccounts(accounts);
      setClientArrangements(arrangements);
      setClientTransactions(allTransactions);
      setPaymentHistory(payments.slice(0, 10)); // Last 10 payments
      
      // Calculate comprehensive financial summary
      const summary = calculateClientFinancialSummary(accounts, arrangements);
      setFinancialSummary(summary);
    } else {
      // Clear data when no client selected
      setClientDebtAccounts([]);
      setClientArrangements([]);
      setClientTransactions([]);
      setPaymentHistory([]);
      setFinancialSummary({
        totalDebtAmount: 0,
        totalAdjustments: 0,
        totalLegalFees: 0,
        totalInterest: 0,
        totalDebtFees: 0,
        totalPayments: 0,
        totalBalance: 0,
        totalArrangementAmount: 0,
        totalArrears: 0,
        accountCount: 0,
        activeArrangementCount: 0,
        lastPaymentDate: null,
        lastPaymentAmount: 0
      });
    }
  }, [clientId, debtAccounts.items, paymentArrangements.items, transactions.items]);

  // Handle view selection
  const handleViewChange = (view) => {
    setSelectedView(view);
  };

  // Render summary overview
  const renderSummaryView = () => (
    <div style={{ padding: '10px' }}>
      {/* Main Financial Totals */}
      <GroupBox label="Financial Overview" style={{ marginBottom: '15px' }}>
        <div style={{ padding: '15px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '15px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '5px' }}>TOTAL DEBT</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#cc0000' }}>
                {formatCurrency(financialSummary.totalDebtAmount)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '5px' }}>TOTAL PAYMENTS</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#006600' }}>
                {formatCurrency(financialSummary.totalPayments)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '5px' }}>CURRENT BALANCE</div>
              <div style={{ 
                fontSize: '18px', 
                fontWeight: 'bold', 
                color: financialSummary.totalBalance > 0 ? '#cc0000' : '#006600' 
              }}>
                {formatCurrency(financialSummary.totalBalance)}
              </div>
            </div>
          </div>
          
          {/* Additional Financial Details */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', fontSize: '11px' }}>
            <div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Adjustments:</strong> {formatCurrency(financialSummary.totalAdjustments)}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Legal Fees:</strong> {formatCurrency(financialSummary.totalLegalFees)}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Interest:</strong> {formatCurrency(financialSummary.totalInterest)}
              </div>
              <div>
                <strong>Debt Fees:</strong> {formatCurrency(financialSummary.totalDebtFees)}
              </div>
            </div>
            <div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Debt Accounts:</strong> {financialSummary.accountCount}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Active Arrangements:</strong> {financialSummary.activeArrangementCount}
              </div>
              {financialSummary.totalArrears > 0 && (
                <div style={{ marginBottom: '8px', color: '#cc0000' }}>
                  <strong>Total Arrears:</strong> {formatCurrency(financialSummary.totalArrears)}
                </div>
              )}
              {financialSummary.lastPaymentDate && (
                <div>
                  <strong>Last Payment:</strong><br/>
                  <span style={{ fontSize: '10px', color: '#666' }}>
                    {new Date(financialSummary.lastPaymentDate).toLocaleDateString()}
                  </span><br/>
                  <span style={{ fontWeight: 'bold' }}>
                    {formatCurrency(financialSummary.lastPaymentAmount)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </GroupBox>

      {/* Recent Payment History */}
      {paymentHistory.length > 0 && (
        <GroupBox label="Recent Payment History">
          <div style={{ padding: '10px' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeadCell>Date</TableHeadCell>
                  <TableHeadCell>Amount</TableHeadCell>
                  <TableHeadCell>Reference</TableHeadCell>
                  <TableHeadCell>Description</TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentHistory.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableDataCell style={{ fontSize: '11px' }}>
                      {new Date(payment.timestamp).toLocaleDateString()}
                    </TableDataCell>
                    <TableDataCell style={{ fontSize: '11px', fontWeight: 'bold', color: '#006600' }}>
                      {formatCurrency(payment.amount)}
                    </TableDataCell>
                    <TableDataCell style={{ fontSize: '11px' }}>
                      {payment.reference || '-'}
                    </TableDataCell>
                    <TableDataCell style={{ fontSize: '11px' }}>
                      {payment.description}
                    </TableDataCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </GroupBox>
      )}
    </div>
  );

  // Render debt accounts view
  const renderAccountsView = () => (
    <div style={{ padding: '10px' }}>
      <GroupBox label={`Debt Accounts (${clientDebtAccounts.length})`}>
        <div style={{ padding: '10px' }}>
          {clientDebtAccounts.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeadCell>Account</TableHeadCell>
                  <TableHeadCell>Debt Amount</TableHeadCell>
                  <TableHeadCell>Adjustments</TableHeadCell>
                  <TableHeadCell>Fees</TableHeadCell>
                  <TableHeadCell>Payments</TableHeadCell>
                  <TableHeadCell>Balance</TableHeadCell>
                  <TableHeadCell>Last Payment</TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clientDebtAccounts.map((account, index) => (
                  <TableRow key={account.id}>
                    <TableDataCell style={{ fontSize: '11px', fontWeight: 'bold' }}>
                      #{index + 1}
                    </TableDataCell>
                    <TableDataCell style={{ fontSize: '11px' }}>
                      {formatCurrency(account.debtAmount)}
                    </TableDataCell>
                    <TableDataCell style={{ fontSize: '11px' }}>
                      {formatCurrency(account.adjustments)}
                    </TableDataCell>
                    <TableDataCell style={{ fontSize: '11px' }}>
                      {formatCurrency((account.legalFees || 0) + (account.interest || 0) + (account.debtFees || 0))}
                    </TableDataCell>
                    <TableDataCell style={{ fontSize: '11px', color: '#006600' }}>
                      {formatCurrency(account.payments)}
                    </TableDataCell>
                    <TableDataCell style={{ 
                      fontSize: '11px', 
                      fontWeight: 'bold',
                      color: account.balance > 0 ? '#cc0000' : '#006600' 
                    }}>
                      {formatCurrency(account.balance)}
                    </TableDataCell>
                    <TableDataCell style={{ fontSize: '10px', color: '#666' }}>
                      {account.lastPaymentDate ? (
                        <>
                          {new Date(account.lastPaymentDate).toLocaleDateString()}<br/>
                          {formatCurrency(account.lastPaymentAmount)}
                        </>
                      ) : '-'}
                    </TableDataCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div style={{ textAlign: 'center', color: '#666', fontStyle: 'italic', padding: '20px' }}>
              No debt accounts found for this client
            </div>
          )}
        </div>
      </GroupBox>
    </div>
  );

  // Render payment arrangements view
  const renderArrangementsView = () => (
    <div style={{ padding: '10px' }}>
      <GroupBox label={`Payment Arrangements (${clientArrangements.length})`}>
        <div style={{ padding: '10px' }}>
          {clientArrangements.length > 0 ? (
            <div>
              {clientArrangements.map((arrangement, index) => (
                <div key={arrangement.id} style={{ 
                  marginBottom: '15px', 
                  padding: '10px', 
                  border: '1px solid #c0c0c0',
                  backgroundColor: arrangement.isActive ? '#f8fff8' : '#f8f8f8'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
                      Arrangement #{index + 1} 
                      <span style={{ 
                        marginLeft: '10px',
                        padding: '2px 6px',
                        fontSize: '10px',
                        backgroundColor: arrangement.isActive ? '#006600' : '#666',
                        color: 'white',
                        borderRadius: '3px'
                      }}>
                        {arrangement.isActive ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>
                    
                    <div style={{ fontSize: '11px', color: '#666' }}>
                      Created: {new Date(arrangement.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    onClick={() => {
                      // We need to pass this up to the parent
                      if (onArrangementEdit) {
                        onArrangementEdit(arrangement);
                      }
                    }}
                    style={{ marginTop: '10px' }}
                  >
                    ✏️ Edit Arrangement
                  </Button>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', fontSize: '11px' }}>
                    <div>
                      <div><strong>Payment Amount:</strong></div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#006600' }}>
                        {formatCurrency(arrangement.amount)}
                      </div>
                      <div style={{ marginTop: '5px' }}>
                        <strong>Frequency:</strong> {arrangement.frequency}
                      </div>
                    </div>
                    <div>
                      <div><strong>Next Due Date:</strong></div>
                      <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        {new Date(arrangement.nextDueDate).toLocaleDateString()}
                      </div>
                      {arrangement.arrears > 0 && (
                        <div style={{ marginTop: '5px', color: '#cc0000' }}>
                          <strong>Arrears:</strong> {formatCurrency(arrangement.arrears)}
                        </div>
                      )}
                    </div>
                    <div>
                      {arrangement.settleForLess && (
                        <>
                          <div><strong>Settlement Option:</strong></div>
                          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#cc6600' }}>
                            {formatCurrency(arrangement.settleAmount)}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#666', fontStyle: 'italic', padding: '20px' }}>
              No payment arrangements found for this client
            </div>
          )}
        </div>
      </GroupBox>
    </div>
  );

  // Render transaction history view
  const renderHistoryView = () => (
    <div style={{ padding: '10px' }}>
      <GroupBox label={`Transaction History (${clientTransactions.length})`}>
        <div style={{ padding: '10px' }}>
          {clientTransactions.length > 0 ? (
            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeadCell>Date</TableHeadCell>
                    <TableHeadCell>Type</TableHeadCell>
                    <TableHeadCell>Amount</TableHeadCell>
                    <TableHeadCell>Description</TableHeadCell>
                    <TableHeadCell>Reference</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientTransactions
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                    .map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableDataCell style={{ fontSize: '10px' }}>
                        {new Date(transaction.timestamp).toLocaleDateString()}
                      </TableDataCell>
                      <TableDataCell style={{ fontSize: '10px' }}>
                        <span style={{
                          padding: '2px 4px',
                          fontSize: '9px',
                          backgroundColor: getTransactionTypeColor(transaction.type),
                          color: 'white',
                          borderRadius: '2px'
                        }}>
                          {transaction.type.toUpperCase()}
                        </span>
                      </TableDataCell>
                      <TableDataCell style={{ 
                        fontSize: '11px', 
                        fontWeight: 'bold',
                        color: getAmountColor(transaction.type, transaction.amount)
                      }}>
                        {transaction.amount ? formatCurrency(transaction.amount) : '-'}
                      </TableDataCell>
                      <TableDataCell style={{ fontSize: '11px' }}>
                        {transaction.description}
                      </TableDataCell>
                      <TableDataCell style={{ fontSize: '10px', color: '#666' }}>
                        {transaction.reference || '-'}
                      </TableDataCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#666', fontStyle: 'italic', padding: '20px' }}>
              No transaction history found for this client
            </div>
          )}
        </div>
      </GroupBox>
    </div>
  );

  // Helper function to get transaction type color
  const getTransactionTypeColor = (type) => {
    switch (type) {
      case TRANSACTION_TYPES.PAYMENT:
        return '#006600';
      case TRANSACTION_TYPES.ADJUSTMENT:
        return '#0066cc';
      case TRANSACTION_TYPES.FEE:
      case TRANSACTION_TYPES.LEGAL_FEE:
      case TRANSACTION_TYPES.DEBT_FEE:
      case TRANSACTION_TYPES.INTEREST:
        return '#cc6600';
      case TRANSACTION_TYPES.CONTACT:
        return '#666666';
      case TRANSACTION_TYPES.ARRANGEMENT:
        return '#6600cc';
      default:
        return '#808080';
    }
  };

  // Helper function to get amount color
  const getAmountColor = (type, amount) => {
    if (!amount) return '#666';
    
    switch (type) {
      case TRANSACTION_TYPES.PAYMENT:
        return '#006600';
      case TRANSACTION_TYPES.ADJUSTMENT:
        return amount > 0 ? '#cc0000' : '#006600';
      default:
        return '#cc0000';
    }
  };

  // Render current view
  const renderCurrentView = () => {
    switch (selectedView) {
      case 'accounts':
        return renderAccountsView();
      case 'arrangements':
        return renderArrangementsView();
      case 'history':
        return renderHistoryView();
      default:
        return renderSummaryView();
    }
  };

  return (
    <Window style={{ width: '100%', minHeight: '600px' }}>
      <WindowHeader>
        <span>💰 Financial Summary{clientId ? ` - Client ${clientId}` : ''}</span>
      </WindowHeader>
      
      {/* Toolbar with view selection */}
      <Toolbar style={{ justifyContent: 'flex-start' }}>
        <Button 
          size="sm" 
          onClick={() => handleViewChange('summary')}
          style={{ backgroundColor: selectedView === 'summary' ? '#0066cc' : undefined }}
        >
          📊 Summary
        </Button>
        <Button 
          size="sm" 
          onClick={() => handleViewChange('accounts')}
          style={{ backgroundColor: selectedView === 'accounts' ? '#0066cc' : undefined }}
        >
          🏦 Accounts
        </Button>
        <Button 
          size="sm" 
          onClick={() => handleViewChange('arrangements')}
          style={{ backgroundColor: selectedView === 'arrangements' ? '#0066cc' : undefined }}
        >
          📅 Arrangements
        </Button>
        <Button 
          size="sm" 
          onClick={() => handleViewChange('history')}
          style={{ backgroundColor: selectedView === 'history' ? '#0066cc' : undefined }}
        >
          📋 History
        </Button>
        <Separator orientation="vertical" />
        <Button size="sm">
          🖨️ Print
        </Button>
        <Button size="sm">
          📧 Export
        </Button>
      </Toolbar>

      <WindowContent>
        {clientId ? (
          renderCurrentView()
        ) : (
          <div style={{ 
            padding: '50px', 
            textAlign: 'center', 
            color: '#666', 
            fontStyle: 'italic' 
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>💰</div>
            <div style={{ fontSize: '16px', marginBottom: '10px' }}>No Client Selected</div>
            <div style={{ fontSize: '12px' }}>
              Select a client to view their financial summary
            </div>
          </div>
        )}
      </WindowContent>
    </Window>
  );
};

export default FinancialSummary;